<template>
  <div class="status">
    <h3>Status</h3>
    <div class="status-container">
      <Dropdown
        label="Category"
        v-model="displayCategory"
        :options="categoryOptions"
        :readonly="isCategoryFromCompany"
        :invalid="showValidationErrors && !displayCategory && !entity.NA && !isCategoryFromCompany"
        @click="isCategoryFromCompany && addAlert('Category is controlled by the company. Edit it from the company\'s page.', 'high', 3)" />
      <Dropdown
        v-if="isCustomer"
        label="Inside Sales"
        v-model="displayInsideSales"
        :options="insideSalesOptions"
        :readonly="isInsideSalesFromCompany || !user.contactsAdmin"
        :showOverride="isInsideSalesFromCompany && user.contactsAdmin"
        :invalid="showValidationErrors && !displayInsideSales && !entity.NA && user.contactsAdmin && !isInsideSalesFromCompany"
        @click="
          user.contactsAdmin ? isInsideSalesFromCompany && addAlert('Inside sales is controlled by the company. Edit it from the company\'s page.', 'high', 3) : addAlert('Must be admin to edit inside sales.', 'high', 3)
        "
        :canBeEmpty="true"
        @override="overrideInsideSales" />
      <Dropdown
        v-if="isCustomer"
        label="Outside Sales"
        v-model="displayOutsideSales"
        :options="outsideSalesOptions"
        :readonly="isOutsideSalesFromCompany || !user.contactsAdmin"
        :showOverride="isOutsideSalesFromCompany && user.contactsAdmin"
        :invalid="showValidationErrors && !displayOutsideSales && !entity.NA && user.contactsAdmin && !isOutsideSalesFromCompany"
        @click="
          user.contactsAdmin
            ? isOutsideSalesFromCompany && addAlert('Outside sales is controlled by the company. Edit it from the company\'s page.', 'high', 3)
            : addAlert('Must be admin to edit outside sales.', 'high', 3)
        "
        :canBeEmpty="true"
        @override="overrideOutsideSales" />
      <MultiDropdown
        v-if="isCustomer"
        label="Customer Details"
        v-model="displayCustomerDetails"
        :options="customerDetailOptions"
        :readonly="isCustomerDetailsFromCompany"
        :invalid="showValidationErrors && !displayCustomerDetails?.length && !entity.NA && !isCustomerDetailsFromCompany"
        @click="isCustomerDetailsFromCompany && addAlert('Customer details is controlled by the company. Edit it from the company\'s page.', 'high', 3)" />
      <MultiDropdown
        v-if="isVendor"
        label="Vendor Department"
        v-model="displayVendorDept"
        :options="vendorDeptOptions"
        :readonly="isVendorDeptFromCompany"
        :invalid="showValidationErrors && !displayVendorDept?.length && !entity.NA && !isVendorDeptFromCompany"
        @click="isVendorDeptFromCompany && addAlert('Vendor department is controlled by the company. Edit it from the company\'s page.', 'high', 3)" />
      <MultiDropdown
        v-if="isVendor"
        label="Vendor Details"
        v-model="displayVendorDetails"
        :options="vendorDetailsOptions"
        :readonly="isVendorDetailsFromCompany"
        :invalid="showValidationErrors && !displayVendorDetails?.length && !entity.NA && !isVendorDetailsFromCompany"
        @click="isVendorDetailsFromCompany && addAlert('Vendor details is controlled by the company. Edit it from the company\'s page.', 'high', 3)" />
      <MultiDropdown
        v-if="isArchitect"
        label="Architect/Engineer Details"
        v-model="displayArchitectDetails"
        :options="architectDetailsOptions"
        :readonly="isArchitectDetailsFromCompany"
        @click="isArchitectDetailsFromCompany && addAlert('Architect/Engineer details is controlled by the company. Edit it from the company\'s page.', 'high', 3)" />
      <Dropdown v-if="isEmployee && type === 'contact'" label="Employee Status" v-model="entity.CUST_Employment_023149966" :options="employeeStatusOptions" :canBeEmpty="true" />
      <Checkbox v-if="type === 'company' && isCustomer" label="Certified Contractor" v-model="entity.CERTIFIEDCONTRACTOR" />
      <Checkbox v-if="type === 'contact' && isCustomer" label="Sales Contact" v-model="entity.SALESCONTACT" />
      <Checkbox v-if="type === 'contact' && isArchitect" label="Primary Contact" v-model="entity.ISPRIMARY" />
    </div>
  </div>
</template>

<script>
import Dropdown from './inputs/Dropdown.vue';
import MultiDropdown from './inputs/MultiDropdown.vue';
import Checkbox from './inputs/Checkbox.vue';
import usePicklistItemsQuery from '../composables/usePicklistItemsQuery';
import useUsersQuery from '../composables/useUsersQuery';
import useAlerts from '../composables/useAlerts';
import { computed } from 'vue';
import useCurrentUser from '../composables/useCurrentUser';

export default {
  name: 'Status',
  components: {
    Dropdown,
    MultiDropdown,
    Checkbox,
  },
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ['company', 'contact'].includes(value),
    },
    entity: {
      type: Object,
      default: null,
    },
    showValidationErrors: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { addAlert } = useAlerts();
    const { user } = useCurrentUser();
    const { salespeople, outsideSalespeople } = useUsersQuery();

    const categoryOptions = computed(() => {
      const baseOptions = usePicklistItemsQuery.pickLists.categories.items.map((item) => item.NAME) || [];
      const currentValue = displayCategory.value;

      // If current value exists and is not in the options, add it to the top
      if (currentValue && !baseOptions.includes(currentValue)) {
        return [currentValue, ...baseOptions];
      }

      return baseOptions;
    });

    const insideSalesOptions = computed(() => {
      return salespeople.map((user) => ({
        value: user.USERID,
        text: user.DISPLAYNAME,
      }));
    });

    const outsideSalesOptions = computed(() => {
      return outsideSalespeople.map((user) => ({
        value: user.USERID,
        text: user.DISPLAYNAME,
      }));
    });

    const customerDetailOptions = computed(() => {
      const baseOptions = usePicklistItemsQuery.pickLists.customerDetail.items.map((item) => item.NAME) || [];
      const currentValues = displayCustomerDetails.value || [];

      // Find selected values that are not in the base options
      const missingValues = currentValues.filter((value) => value && !baseOptions.includes(value));

      // Add missing values to the top of the list
      return [...missingValues, ...baseOptions];
    });

    const vendorDeptOptions = computed(() => {
      const baseOptions = usePicklistItemsQuery.pickLists.vendorDepartments.items.map((item) => item.NAME) || [];
      const currentValues = displayVendorDept.value || [];

      // Find selected values that are not in the base options
      const missingValues = currentValues.filter((value) => value && !baseOptions.includes(value));

      // Add missing values to the top of the list
      return [...missingValues, ...baseOptions];
    });

    const vendorDetailsOptions = computed(() => {
      const baseOptions = usePicklistItemsQuery.pickLists.vendorDetails.items.map((item) => item.NAME) || [];
      const currentValues = displayVendorDetails.value || [];

      // Find selected values that are not in the base options
      const missingValues = currentValues.filter((value) => value && !baseOptions.includes(value));

      // Add missing values to the top of the list
      return [...missingValues, ...baseOptions];
    });

    const architectDetailsOptions = computed(() => {
      const baseOptions = usePicklistItemsQuery.pickLists.architectDetails.items.map((item) => item.NAME) || [];
      const currentValues = displayArchitectDetails.value || [];

      // Find selected values that are not in the base options
      const missingValues = currentValues.filter((value) => value && !baseOptions.includes(value));

      // Add missing values to the top of the list
      return [...missingValues, ...baseOptions];
    });

    // Helper function to check if we should use company data for contacts
    const shouldUseCompanyData = (contactValue, hasCompany) => {
      return props.type === 'contact' && hasCompany;
    };

    // Helper function specifically for salespeople - only use company data if no contact data
    const shouldUseCompanyDataForSales = (contactValue, hasCompany) => {
      return props.type === 'contact' && hasCompany && !contactValue;
    };

    // Computed properties for display values and disabled states
    const hasCompany = computed(() => {
      return props.entity?.company && Object.keys(props.entity.company).length > 0;
    });

    // Category - always use company data if available for contacts
    const displayCategory = computed({
      get() {
        if (shouldUseCompanyData(props.entity?.CATEGORY, hasCompany.value)) {
          return props.entity.company.CATEGORY;
        }
        return props.entity?.CATEGORY;
      },
      set(value) {
        if (props.entity) {
          props.entity.CATEGORY = value;
        }
      },
    });

    const isCategoryFromCompany = computed(() => {
      return shouldUseCompanyData(props.entity?.CATEGORY, hasCompany.value);
    });

    // Inside Sales - use contact data if exists, otherwise company data
    const displayInsideSales = computed({
      get() {
        if (props.entity?.INSIDESALES) {
          return props.entity.INSIDESALES;
        }
        if (shouldUseCompanyDataForSales(props.entity?.INSIDESALES, hasCompany.value)) {
          return props.entity.company.INSIDESALES;
        }
        return props.entity?.INSIDESALES;
      },
      set(value) {
        if (props.entity) {
          props.entity.INSIDESALES = value || null;
        }
      },
    });

    const isInsideSalesFromCompany = computed(() => {
      return shouldUseCompanyDataForSales(props.entity?.INSIDESALES, hasCompany.value);
    });

    // Outside Sales - use contact data if exists, otherwise company data
    const displayOutsideSales = computed({
      get() {
        if (props.entity?.OUTSIDESALES) {
          return props.entity.OUTSIDESALES;
        }
        if (shouldUseCompanyDataForSales(props.entity?.OUTSIDESALES, hasCompany.value)) {
          return props.entity.company.OUTSIDESALES;
        }
        return props.entity?.OUTSIDESALES;
      },
      set(value) {
        if (props.entity) {
          props.entity.OUTSIDESALES = value || null;
        }
      },
    });

    const isOutsideSalesFromCompany = computed(() => {
      return shouldUseCompanyDataForSales(props.entity?.OUTSIDESALES, hasCompany.value);
    });

    // Customer Details - always use company data if available for contacts
    const displayCustomerDetails = computed({
      get() {
        if (shouldUseCompanyData(props.entity?.CUSTOMERDETAILS, hasCompany.value)) {
          return props.entity.company.CUSTOMERDETAILS || [];
        }
        return props.entity?.CUSTOMERDETAILS || [];
      },
      set(value) {
        if (props.entity) {
          props.entity.CUSTOMERDETAILS = value;
        }
      },
    });

    const isCustomerDetailsFromCompany = computed(() => {
      return shouldUseCompanyData(props.entity?.CUSTOMERDETAILS, hasCompany.value);
    });

    // Vendor Dept - always use company data if available for contacts
    const displayVendorDept = computed({
      get() {
        if (shouldUseCompanyData(props.entity?.VENDORDEPT, hasCompany.value)) {
          return props.entity.company.VENDORDEPT || [];
        }
        return props.entity?.VENDORDEPT || [];
      },
      set(value) {
        if (props.entity) {
          props.entity.VENDORDEPT = value;
        }
      },
    });

    const isVendorDeptFromCompany = computed(() => {
      return shouldUseCompanyData(props.entity?.VENDORDEPT, hasCompany.value);
    });

    // Vendor Details - always use company data if available for contacts
    const displayVendorDetails = computed({
      get() {
        if (shouldUseCompanyData(props.entity?.VENDORDETAILS, hasCompany.value)) {
          return props.entity.company.VENDORDETAILS || [];
        }
        return props.entity?.VENDORDETAILS || [];
      },
      set(value) {
        if (props.entity) {
          props.entity.VENDORDETAILS = value;
        }
      },
    });

    const displayArchitectDetails = computed({
      get() {
        if (shouldUseCompanyData(props.entity?.ARCHITECTDETAILS, hasCompany.value)) {
          return props.entity.company.ARCHITECTDETAILS || [];
        }
        return props.entity?.ARCHITECTDETAILS || [];
      },
      set(value) {
        if (props.entity) {
          props.entity.ARCHITECTDETAILS = value;
        }
      },
    });

    const isArchitectDetailsFromCompany = computed(() => {
      return shouldUseCompanyData(props.entity?.ARCHITECTDETAILS, hasCompany.value);
    });

    const isVendorDetailsFromCompany = computed(() => {
      return shouldUseCompanyData(props.entity?.VENDORDETAILS, hasCompany.value);
    });

    return {
      categoryOptions,
      insideSalesOptions,
      outsideSalesOptions,
      customerDetailOptions,
      vendorDeptOptions,
      vendorDetailsOptions,
      architectDetailsOptions,
      displayCategory,
      displayInsideSales,
      displayOutsideSales,
      displayCustomerDetails,
      displayVendorDept,
      displayVendorDetails,
      displayArchitectDetails,
      isCategoryFromCompany,
      isInsideSalesFromCompany,
      isOutsideSalesFromCompany,
      isCustomerDetailsFromCompany,
      isVendorDeptFromCompany,
      isVendorDetailsFromCompany,
      isArchitectDetailsFromCompany,
      addAlert,
      user,
    };
  },
  data() {
    return {
      employeeStatusOptions: ['Current', 'Laid Off', 'Prospective', 'Quit', 'Seasonal', 'Terminated'],
    };
  },
  computed: {
    isCustomer() {
      // Use the display category which might come from company
      const category = this.displayCategory;
      return category?.includes('Customer');
    },
    isVendor() {
      // Use the display category which might come from company
      const category = this.displayCategory;
      return category?.includes('Vendor');
    },
    isEmployee() {
      const category = this.displayCategory;
      return category?.includes('Employee');
    },
    isArchitect() {
      const category = this.displayCategory;
      return category?.includes('Architect') || category?.includes('Engineer');
    },
  },
  methods: {
    overrideInsideSales() {
      this.entity.INSIDESALES = this.entity.company.INSIDESALES;
    },
    overrideOutsideSales() {
      this.entity.OUTSIDESALES = this.entity.company.OUTSIDESALES;
    },
  },
};
</script>

<style scoped lang="scss">
.status {
  flex: 2 0 18rem;
  max-width: 25rem;

  .status-container {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    gap: 1rem;
    margin-top: 1.5rem;
  }
}
</style>
